import React, { useEffect, useRef } from 'react';

interface CKEDITORStatic {
    replace: (element: HTMLTextAreaElement, config?: object) => CKEditorInstance;
    ENTER_BR: number;
}

declare global {
    interface Window {
        CKEDITOR: CKEDITORStatic | undefined;
    }
}

interface CKEditorInstance {
    setData: (data: string) => void;
    getData: () => string;
    on: (event: string, callback: () => void) => void;
    destroy: () => void;
}

interface RichTextEditorProps {
    value: string;
    onChange: (data: string) => void;
    className?: string;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, className }) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const editorInstance = useRef<CKEditorInstance | null>(null);

    useEffect(() => {
        let destroyed = false;

        function initEditor() {
            if (window.CKEDITOR && textareaRef.current && !editorInstance.current) {
                editorInstance.current = window.CKEDITOR.replace(textareaRef.current, {
                    toolbar: [
                        { name: 'clipboard', items: ['Undo', 'Redo'] },
                        { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline'] },
                        { name: 'insert', items: ['Image'] },
                        { name: 'links', items: ['Link', 'Unlink'] }
                    ],
                    removePlugins: 'elementspath',
                    resize_enabled: false,
                    enterMode: window.CKEDITOR.ENTER_BR,
                    shiftEnterMode: window.CKEDITOR.ENTER_BR,
                    height: '400px',
                });
                editorInstance.current.setData(value);
                editorInstance.current.on('change', () => {
                    if (!destroyed) {
                        const data = editorInstance.current ? editorInstance.current.getData() : '';
                        onChange(data);
                    }
                });
            }
        }

        if (!window.CKEDITOR) {
            const script = document.createElement('script');
            script.src = '/ckeditor/ckeditor.js';
            script.async = true;
            script.onload = initEditor;
            document.body.appendChild(script);
            return () => {
                destroyed = true;
                script.remove();
                editorInstance.current?.destroy();
                editorInstance.current = null;
            };
        } else {
            initEditor();
            return () => {
                destroyed = true;
                editorInstance.current?.destroy();
                editorInstance.current = null;
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (
            editorInstance.current !== null &&
            editorInstance.current.getData() !== value
        ) {
            editorInstance.current.setData(value);
        }
    }, [value]);

    return (
        <div className={className}>
            <textarea ref={textareaRef} defaultValue={value} />
        </div>
    );
};
